<template>
    <div>
        <section>
            <div class="container">
                <img :src="productLine.logo" :title="productLine.name" class="img-logo" /><sup class="text-black text-h5 text-weight-bold on-right" style="vertical-align: top">{{ productLine.year }}</sup>
                <div class="row q-col-gutter-lg justify-between q-pt-sm">
                    <div class="col-xs-12 col-md-7 q-mb-md">
                        <p>
                            HeatCAD {{ productLine.year }} is a drawing-based software for fast and accurate calculation of residential heating and cooling loads.
                            The <strong>Professional Edition</strong> supports ASHRAE and CSA residential heat loss calculations.
                            The <strong>MJ8 Edition</strong> provides <router-link to="/acca">ACCA<span style="top: -0.4em; font-size: 0.8em; position: relative;">&reg;</span>-Approved Manual J<span style="top: -0.4em; font-size: 0.8em; position: relative;">&reg;</span> (8th Edition)</router-link>
                            calculations for residential heating and cooling loads. And HeatCAD is verified for
                            <router-link to="/f280">CSA F280-12</router-link> residential heating and cooling load calculations.
                            HeatCAD delivers advanced design features including integrated load calculations, automatic detection of unheated surfaces, and 3D CAD views. <router-link to="#trial">Try it now free for 30-days</router-link>.
                        </p>
                        <div class="row q-col-gutter-sm">
                            <div class="col-xs-12 col-sm-auto">
                                <q-btn color="primary"
                                       :to="product.tutorial"
                                       label="Tutorial"
                                       icon="mdi-school"
                                       class="full-width" />
                            </div>
                            <div class="col-xs-12 col-sm-auto">
                                <q-btn class="full-width bg-white text-primary"
                                       style="border: 1px solid currentColor; height: 95%;"
                                       label="Free Trial"
                                       icon="mdi-download"
                                       @click="ScrollTo($refs.trialRef, '#trial')" />
                            </div>
                            <div class="col-xs-12 col-sm-auto">
                                <q-btn label="Pricing" color="positive" icon="mdi-cart"
                                       :to="{ name:'/store', params: { productLine: 'heatcad'}}"
                                       class="full-width" />
                            </div>
                            <div class="col-xs-12 col-sm-auto">
                                <q-btn label="Activate" color="positive" icon="mdi-lock-open-variant"
                                       to="/activation/heatcad"
                                       class="full-width" />
                            </div>
                            <div class="lt-md col-xs-12 col-sm-auto">
                                <q-btn label="View Demo" color="secondary" icon="mdi-video"
                                       class="full-width"
                                       @click="showVideo = true" />
                            </div>
                        </div>
                        <p class="text-caption text-grey q-mt-lg q-mb-none">
                            HeatCAD is intended for use by professionals in the HVAC industry that have knowledge and experience with the calculations required for proper heating and/or cooling system design. It is not intended for DIY users that may not be adequately trained in these areas.
                        </p>
                    </div>

                    <div class="gt-sm col-xs-12 col-md-5">
                        <div class="column" :class="{'items-end' : !screenSize.xs, 'items-center' : screenSize.lt.md}">
                            <div @click="showVideo = true" class="cursor-pointer demo text-center">
                                <img src="https://www.avenir-online.com/AvenirWeb/HeatCAD/Tutorials/HeatCADIntroduction2016/HeatCAD_Introduction_2016_First_Frame.png" class="img-intro" title="Introduction" />
                                <div class="overlay"><q-icon name="mdi-play-circle" color="grey-7" size="5em"></q-icon></div>
                            </div>
                        </div>
                        <dialog-video-player :show.sync="showVideo" title="Introduction to HeatCAD" src="https://www.avenir-online.com/AvenirWeb/HeatCAD/Tutorials/HeatCADIntroduction2016/HeatCAD%20Introduction_2016.mp4" />
                    </div>
                </div>
            </div>
        </section>

        <section>
            <div class="container">
                <div class="text-title">Editions</div>
                <p>
                    HeatCAD is available in two different editions to best match your needs.
                    For a list of functionality and new features in each edition, view the <a href="https://www.avenir-online.com/AvenirWeb/Pdfs/HeatCAD%20Features.pdf" target="_blank">Feature Comparison PDF</a>.
                    The video demo provides a quick introduction, and the <router-link to="/support/tutorial?productLine=heatcad">Tutorial</router-link> lessons provide a much more in-depth look.
                </p>
                <div class="row q-col-gutter-lg items-stretch justify-center q-mt-md">

                    <div class="col-xs-12 col-md-4">
                        <q-card class="full-height">
                            <q-card-section class="bg-primary text-white text-center q-pa-sm">
                                <div class="text-subtitle1 q-pa-xs">Professional Edition</div>
                            </q-card-section>

                            <q-separator />

                            <q-card-section>
                                <ul :class="{'q-px-lg' : screenSize.xs, 'q-px-xl' : !screenSize.xs}">
                                    <li>Floorplan drawing and importing (PDF, AutoCAD, JPG)</li>
                                    <li>Automated heat loss calculations as you draw</li>
                                    <li>ASHRAE residential heat loss calculations</li>
                                    <li>Verified CSA F280-12 residential heating and cooling load calculations <router-link to="/f280" style="font-size: 0.95em;">(more info...)</router-link></li>
                                    <li>Files compatible with LoopCAD<span style="top: -0.4em; font-size: 0.8em; position: relative;">&reg;</span> for radiant designs</li>
                                    <li>3D drawing views</li>
                                </ul>
                            </q-card-section>
                        </q-card>
                    </div>
                    <div class="col-xs-12 col-md-4">
                        <q-card class="full-height">
                            <q-card-section class="bg-primary text-white text-center q-pa-sm">
                                <div class="text-subtitle1 q-pa-xs">MJ8 Edition</div>
                            </q-card-section>

                            <q-separator />

                            <q-card-section>
                                <p :class="{'q-px-sm' : screenSize.xs, 'q-px-lg' : !screenSize.xs}">All features in Professional Edition, plus...</p>
                                <ul :class="{'q-px-lg' : screenSize.xs, 'q-px-xl' : !screenSize.xs}">
                                    <li>ACCA-Approved Manual J (8th Edition) residential heating and cooling load calculations <router-link to="/acca" style="font-size: 0.95em;">(more info...)</router-link></li>
                                </ul>
                            </q-card-section>
                        </q-card>
                    </div>
                </div>
                <div class="row col justify-center q-mt-lg">
                    <q-btn label="Feature Comparison PDF" color="primary" icon="mdi-file" href="https://www.avenir-online.com/AvenirWeb/Pdfs/HeatCAD%20Features.pdf" target="_blank" />
                </div>
            </div>
        </section>

        <features :productLine="productLine" />
        <div id="trial" ref="trialRef">
            <free-trial :productLine="productLine" />
        </div>
        <license-subscription-info :productLine="productLine" />
        <system-requirements :sysReq="productLine.sysReq" />
    </div>
</template>
<script>
    import licenseSubscriptionInfo from '@/components/LicenseSubscriptionInfo'
    import systemRequirements from '@/components/SystemRequirements'
    import features from '@/components/ProductFeatures'
    import freeTrial from '@/components/FreeTrial'
    import { productTypes } from '@/plugins/productTypes'
    import { productLines } from '@/plugins/productLines'
    import dialogVideoPlayer from '@/components/dialogs/DialogVideoPlayer'
    export default {
        name: 'heatcad',
        components: {
            dialogVideoPlayer,
            licenseSubscriptionInfo,
            systemRequirements,
            features,
            freeTrial,
        },
        data() {
            return {
                showVideo: false,
                productLine: productLines.HeatCAD,
                product: productTypes[0].products?.find(({ name }) => name === 'HeatCAD'),
            }
        },
        methods: {
            ScrollTo(element, hash) {
                const currentHash = this.$route.hash;
                const targetHash = hash;

                if (currentHash !== targetHash) {
                    this.scrollToElement(element);
                    this.$router.push({ path: this.$route.path + "/", hash: targetHash });
                } else {
                    this.scrollToElement(element);
                }
            },
        },
        computed: {
            screenSize() {
                return this.$q.screen
            },
        },
    }
</script>
<style lang="scss" scoped>
    @import "../../styles/quasar.variables.scss";

    @media (max-width: $breakpoint-xs-max) {
        .img-logo {
            max-height: 55px;
        }
    }

    .img-intro {
        width: 450px;
        width: 100%;
    }

    .overlay {
        position: absolute;
        top: 30%;
        bottom: 0;
        left: 0;
        right: 0;
    }

    .demo {
        position: relative;
        max-width: 450px;
    }
</style>
